//
// # Add Symbol Pin / Expression Label / Catalogue Shape / Selection Box Mode Handlers
//
// Provisional modes: the entity follows the mouse until the user clicks to commit.
//

// Local Imports
import { schdata, graphics, Point } from "GenAlphaModel";
import * as changes from "DrawCore/src/changes";
import { SymbolPin, ExprLabel, Annotation } from "DrawCore/src/content/shared";
import { Annotations } from "../../shared/annotations";
import { Primitive } from "DrawCore/src/primitives";
import type { Symbol } from "../symbol";
import { SelectionBoxEntity } from "DrawCore/src/content/symbol/selectionbox";
import { ModeHandler } from "../../../modes";
import { Keys } from "../../../keys";
import { StyleTables } from "DrawCore/src/content/shared/styles";
import type { RedrawRequest } from "DrawCore/src/redraw";

// # Add Symbol Pin Mode
// The provisional pin follows the mouse and is committed on click.
export class AddSymbolPin extends ModeHandler {
  private constructor(readonly symbol: Symbol, readonly pin: SymbolPin) {
    super();
  }
  get editor() {
    return this.symbol.editor;
  }

  // Enter this mode, creating a provisional pin from the last-used template.
  static enter(symbol: Symbol): unknown {
    const { lastSymbolPinData } = symbol;
    const newPinData = schdata.SymbolPin.clone(lastSymbolPinData);
    const pin = new SymbolPin({ data: newPinData, parent: symbol.render });
    return AddSymbolPin.enterWith(symbol, pin);
  }

  // Enter this mode with a pre-configured provisional pin (e.g. from the Add Pin dialog).
  // The pin is *not* added to the symbol yet — it follows the mouse until committed.
  static enterWith(symbol: Symbol, pin: SymbolPin): unknown {
    symbol.render.provisionals.add(pin);
    symbol.select(pin);
    return symbol.setMode(new AddSymbolPin(symbol, pin));
  }

  // On abort, remove the provisional pin and return to `Idle`.
  override abort = () => {
    this.symbol.render.provisionals.delete(this.pin);
    this.symbol.deselect();
    this.pin.abort();
    this.editor.goUiIdle();
  };

  // Update the location of our in-progress pin.
  updateLoc = () => {
    const { pin } = this;
    return pin.updateLoc(this.editor.gridPointNearestToMouse());
  };

  // Update the location on mouse-move.
  override handleMouseMove = () => this.updateLoc();

  // Add the currently-pending entity to the symbol.
  commit = () => {
    const { editor, symbol, pin } = this;

    // Remove from provisionals before committing to the real data store.
    symbol.render.provisionals.delete(pin);

    // Add it to the symbol.
    symbol.addEntity(pin);

    // Notify the changeLog and platform of the change.
    symbol.logChange(changes.Add.new(pin));

    // Return to idle mode.
    editor.goUiIdle();
  };

  // Commit the pin on mouse-up, i.e. the end of a click to place it.
  override handleMouseUp = () => this.commit();

  override handleKey = (e: KeyboardEvent) => {
    if (e.key === Keys.Escape) return this.abort();
    if (e.key === Keys.Enter) return this.commit();
  };
}

// # Add Expression Label Mode
// Provisional mode for placing an expression label.
// The label follows the mouse until the user clicks to commit.
export class AddExprLabel extends ModeHandler {
  private constructor(readonly symbol: Symbol, readonly label: ExprLabel) {
    super();
  }
  get editor() {
    return this.symbol.editor;
  }

  // Enter this mode with a pre-configured ExprLabel.
  static enterWith(symbol: Symbol, label: ExprLabel): unknown {
    symbol.render.provisionals.add(label);
    symbol.select(label);
    return symbol.setMode(new AddExprLabel(symbol, label));
  }

  override abort = () => {
    this.symbol.render.provisionals.delete(this.label);
    this.symbol.deselect();
    this.label.abort();
    this.editor.goUiIdle();
  };

  override handleMouseMove = () => {
    const loc = this.editor.gridPointNearestToMouse();
    return this.label.update({ loc });
  };

  override handleMouseUp = () => this.commit();

  override handleKey = (e: KeyboardEvent) => {
    if (e.key === Keys.Escape) return this.abort();
    if (e.key === Keys.Enter) return this.commit();
  };

  private commit = () => {
    const { editor, symbol, label } = this;
    symbol.render.provisionals.delete(label);
    symbol.addEntity(label);
    symbol.logChange(changes.Add.new(label));
    editor.goUiIdle();
  };
}

// # Add Catalogue Shape Mode
// Provisional mode for placing a multi-element graphics shape from the catalogue.
// All primitives follow the mouse as a group until the user clicks to commit.
export class AddCatalogueShape extends ModeHandler {
  private readonly annotations: Annotations;
  private readonly primitives: Array<Primitive>;
  private readonly origLocs: Array<Point>;
  private readonly refLoc: Point;

  private constructor(readonly symbol: Symbol, primitives: Array<Primitive>) {
    super();
    this.annotations = symbol.annotations;
    this.primitives = primitives;
    this.origLocs = primitives.map((p) => Point.clone(p.loc));
    // Use the first primitive's location as the reference point.
    this.refLoc = Point.clone(primitives[0].loc);
  }
  get editor() {
    return this.symbol.editor;
  }

  // Enter this mode: create provisional primitives from catalogue elements.
  static enter(symbol: Symbol, elements: Array<graphics.Element>): unknown {
    const annotations = symbol.annotations;
    const primitives: Array<Primitive> = [];

    for (const elem of elements) {
      const prim = annotations.graphicsEntity.add(elem) as Primitive;
      primitives.push(prim);
      // Add as provisional Annotation so it renders
      symbol.render.provisionals.add(new Annotation(prim));
    }

    if (primitives.length === 0) return null;
    return symbol.setMode(new AddCatalogueShape(symbol, primitives));
  }

  override handleMouseMove = () => {
    const { editor, primitives, origLocs, refLoc } = this;
    const mousePos = editor.gridPointNearestToMouse();
    const delta = Point.sub(mousePos, refLoc);

    for (let i = 0; i < primitives.length; i++) {
      primitives[i].updateLoc({
        x: origLocs[i].x + delta.x,
        y: origLocs[i].y + delta.y,
      });
    }
  };

  override handleMouseUp = () => this.commit();

  override handleKey = (e: KeyboardEvent) => {
    if (e.key === Keys.Escape) return this.abort();
    if (e.key === Keys.Enter) return this.commit();
  };

  override abort = () => {
    const { symbol, primitives, annotations } = this;
    // Remove all provisional primitives
    for (const prim of primitives) {
      annotations.graphicsEntity.remove(prim);
    }
    symbol.render.provisionals.clear();
    symbol.deselect();
    this.editor.goUiIdle();
  };

  private commit = () => {
    const { editor, symbol, annotations, primitives } = this;
    const singleChanges: Array<changes.Single<changes.ChangeEntity>> = [];

    // Clear provisionals — these are now real entities
    symbol.render.provisionals.clear();

    for (const prim of primitives) {
      const anno = new Annotation(prim);
      singleChanges.push(changes.add(anno));
      annotations.registerGroup(prim.data);
    }

    // Log all additions as a single atomic change
    if (singleChanges.length === 1) {
      symbol.logChange(singleChanges[0] as changes.Change<never>);
    } else if (singleChanges.length > 1) {
      const combo = changes.Combo.fromArray(singleChanges);
      if (combo.ok) symbol.logChange(combo.val as changes.Change<never>);
    }

    editor.requestRedraw();
    editor.goUiIdle();
  };
}

// # Add Selection Box Mode
//
// Two-phase draw: click to place one corner, drag to the opposite corner.
// On commit, the SelectionBoxEntity is added to the symbol and logged
// through the change pipeline so it propagates to AlphaServer.

const SELBOX_STATUS_KEY = "add-selection-box";

// Phase 1: wait for the user to click, placing the first corner.
export class AddSelectionBox extends ModeHandler {
  private constructor(readonly symbol: Symbol) {
    super();
  }
  get editor() {
    return this.symbol.editor;
  }

  static enter(symbol: Symbol): unknown {
    symbol.editor.overlays.setStatusMessage(
      "Drawing Selection Box (Escape to cancel)",
      SELBOX_STATUS_KEY
    );
    return symbol.setMode(new AddSelectionBox(symbol));
  }

  // Place the first corner on mouse-up.
  override handleMouseUp = () => {
    const { editor, symbol } = this;
    const point = editor.gridPointNearestToMouse();
    const rect = graphics.Rectangle.new({
      bbox: schdata.Bbox.fromTwoPoints([
        schdata.Point.clone(point),
        schdata.Point.clone(point),
      ]),
      filled: false,
    });

    const boxEntity = new SelectionBoxEntity({
      data: rect,
      parent: symbol.render,
      table: StyleTables.symbolSelectionBox,
    });

    // Add as provisional so it renders during drag
    symbol.render.provisionals.add(boxEntity);

    return editor.setMode(new AddSelectionBoxPart2(symbol, boxEntity, point));
  };

  override abort = () => {
    this.editor.overlays.clearStatusMessage(SELBOX_STATUS_KEY);
    return this.editor.goUiIdle();
  };
}

// Phase 2: drag the opposite corner, commit on mouse-up.
class AddSelectionBoxPart2 extends ModeHandler {
  constructor(
    readonly symbol: Symbol,
    readonly boxEntity: SelectionBoxEntity,
    readonly fixedPoint: schdata.Point
  ) {
    super();
  }
  get editor() {
    return this.symbol.editor;
  }

  private updateLoc = (): RedrawRequest => {
    const point = this.editor.gridPointNearestToMouse();
    return this.boxEntity.update({
      bbox: schdata.Bbox.fromTwoPoints([this.fixedPoint, point]),
    });
  };

  override handleMouseMove = () => this.updateLoc();

  // Commit on mouse-up.
  override handleMouseUp = () => {
    const { editor, symbol, boxEntity } = this;
    this.updateLoc();

    // Move from provisionals to the real data store.
    symbol.render.provisionals.delete(boxEntity);
    symbol.addEntity(boxEntity);

    // Log the change so it propagates to AlphaServer.
    symbol.logChange(changes.Add.new(boxEntity));

    editor.overlays.clearStatusMessage(SELBOX_STATUS_KEY);
    return editor.goUiIdle();
  };

  // Abort: remove the provisional box, return to idle.
  override abort = () => {
    this.symbol.render.provisionals.delete(this.boxEntity);
    this.editor.overlays.clearStatusMessage(SELBOX_STATUS_KEY);
    return this.editor.goUiIdle();
  };
}
